import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Building2,
  Users,
  ImageIcon,
  MessageSquareQuote,
  FileText,
  UserRound,
  Activity,
  TrendingUp,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

import { adminApi } from "@/lib/api";

const LEAD_COLORS = ["#E9C947", "#77CA65", "#6D88F2"];

export default function AdminDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    adminApi.metrics().then(setMetrics).catch(() => setMetrics(null));
    adminApi.activity({ limit: 20 }).then(setActivity).catch(() => setActivity([]));
  }, []);

  if (!metrics) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 bg-ink-graphite rounded-sm animate-pulse" />
        <div className="grid md:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-28 bg-ink-graphite rounded-sm animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const kpis = metrics.kpis || {};
  const leadsByType = (metrics.leads_by_type || []).filter((d) => d.value > 0);
  const totalLeads = leadsByType.reduce((s, x) => s + x.value, 0);
  const pageViews = metrics.page_views || [];

  return (
    <div className="space-y-8">
      {/* Hero card */}
      <div className="relative overflow-hidden admin-card rounded-sm p-6 md:p-10">
        <div className="absolute inset-0 blueprint opacity-20" />
        <div className="relative z-10 flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="eyebrow">[ Dashboard ]</p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mt-3">
              Welcome back.
            </h2>
            <p className="body-text mt-3 max-w-lg">
              Manage Minga Architecture’s website, projects and inquiries.
            </p>
          </div>
          <div className="text-right">
            <p className="eyebrow-muted">[ Today ]</p>
            <p className="text-lg font-semibold tracking-tight mt-1">
              {new Date().toLocaleDateString("en-US", {
                weekday: "long",
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </p>
          </div>
        </div>

        <div className="relative z-10 mt-8 flex flex-wrap gap-3">
          <QuickAction to="/admin/projects" label="Add Project" />
          <QuickAction to="/admin/pages" label="Edit Homepage" />
          <QuickAction to="/admin/media" label="Upload Media" />
          <QuickAction to="/admin/leads" label="Review Leads" />
          <QuickAction to="/admin/testimonials" label="Add Testimonial" />
        </div>
      </div>

      {/* KPIs */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Total Leads" value={kpis.total_leads} accent="yellow" icon={Users} />
        <KpiCard label="New Leads" value={kpis.new_leads} accent="green" icon={TrendingUp} />
        <KpiCard label="Published Projects" value={kpis.published_projects} accent="yellow" icon={Building2} />
        <KpiCard label="Draft Content" value={(kpis.draft_projects || 0) + (kpis.testimonials_draft || 0)} accent="red" icon={FileText} />
        <KpiCard label="Testimonials" value={kpis.testimonials_published} accent="yellow" icon={MessageSquareQuote} />
        <KpiCard label="Team Members" value={kpis.total_team} accent="green" icon={UserRound} />
        <KpiCard label="Media Library" value={kpis.total_media} accent="yellow" icon={ImageIcon} />
        <KpiCard
          label="Total Visits"
          value={kpis.total_visits}
          accent="green"
          icon={Activity}
          note={metrics.analytics_connected ? undefined : "Internal counter (pending analytics)"}
        />
      </div>

      {/* Charts & lists */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Leads by type */}
        <div className="admin-card p-5 md:p-6 rounded-sm">
          <div className="flex items-center justify-between">
            <p className="eyebrow-muted">[ Leads by Type ]</p>
            <Link to="/admin/leads" className="text-xs text-brand-gray hover:text-brand-yellow inline-flex items-center gap-1">
              View all <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="h-56 mt-4">
            {totalLeads > 0 ? (
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={leadsByType}
                    dataKey="value"
                    innerRadius={45}
                    outerRadius={80}
                    stroke="#0a0a0b"
                    strokeWidth={3}
                  >
                    {leadsByType.map((_, i) => (
                      <Cell key={i} fill={LEAD_COLORS[i % LEAD_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: "#111",
                      border: "1px solid #26262a",
                      color: "#FAFAFA",
                      fontSize: 12,
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState label="No lead data yet. Submissions will appear here." />
            )}
          </div>
          <div className="mt-4 space-y-1.5">
            {leadsByType.map((l, i) => (
              <div key={l.name} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2">
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ background: LEAD_COLORS[i % LEAD_COLORS.length] }}
                  />
                  {l.name}
                </span>
                <span className="font-semibold">{l.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Page views */}
        <div className="admin-card p-5 md:p-6 rounded-sm lg:col-span-2">
          <div className="flex items-center justify-between">
            <p className="eyebrow-muted">[ Pages Performance ]</p>
            <span className="text-xs text-brand-gray">
              {metrics.analytics_connected ? "Live" : "Internal counters • pending analytics"}
            </span>
          </div>
          <div className="h-56 mt-4">
            {pageViews.length > 0 ? (
              <ResponsiveContainer>
                <BarChart data={pageViews.map((p) => ({ ...p, page: (p.page || "/").slice(0, 20) }))}>
                  <CartesianGrid stroke="#1f1f22" vertical={false} />
                  <XAxis dataKey="page" stroke="#8a8a8f" fontSize={11} />
                  <YAxis stroke="#8a8a8f" fontSize={11} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      background: "#111",
                      border: "1px solid #26262a",
                      color: "#FAFAFA",
                      fontSize: 12,
                    }}
                    cursor={{ fill: "rgba(255,255,255,0.03)" }}
                  />
                  <Bar dataKey="views" fill="#E9C947" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState label="Pending analytics connection. Data will appear here once tracking is configured." />
            )}
          </div>
        </div>
      </div>

      {/* Activity + Content Health */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="admin-card rounded-sm p-5 md:p-6 lg:col-span-2">
          <div className="flex items-center justify-between">
            <p className="eyebrow-muted">[ Recent Activity ]</p>
            <Link to="/admin/activity" className="text-xs text-brand-gray hover:text-brand-yellow inline-flex items-center gap-1">
              View all <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          <ul className="mt-6 space-y-4">
            {activity.length === 0 && (
              <li>
                <EmptyState label="No recorded activity yet." small />
              </li>
            )}
            {activity.slice(0, 10).map((a) => (
              <li key={a.id} className="flex items-start gap-3">
                <div className="h-2 w-2 rounded-full bg-brand-yellow mt-2 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-brand-white">{a.summary}</p>
                  <p className="text-xs text-brand-gray mt-0.5">
                    <span className="uppercase tracking-wider">{a.module}</span> ·{" "}
                    {a.user || "system"} · {new Date(a.created_at).toLocaleString()}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="admin-card rounded-sm p-5 md:p-6">
          <p className="eyebrow-muted">[ Content Health ]</p>
          <ul className="mt-6 space-y-3 text-sm">
            <HealthRow label="Missing project cover" value={metrics.content_health?.missing_project_images} />
            <HealthRow label="Draft testimonials" value={metrics.content_health?.draft_testimonials} />
            <HealthRow label="Missing SEO descriptions" value={metrics.content_health?.missing_seo_descriptions} />
            <HealthRow label="Missing team portraits" value={metrics.content_health?.missing_team_portraits} />
            <HealthRow label="Pending disclaimer text" value={metrics.content_health?.pending_disclaimer ? "Pending" : "OK"} pending />
            <HealthRow label="Form backend configured" value={metrics.content_health?.form_backend_configured ? "OK" : "Pending"} pending />
          </ul>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, note, accent = "yellow" }) {
  const accentBg =
    accent === "green"
      ? "bg-brand-green/12 text-brand-green"
      : accent === "red"
        ? "bg-brand-red/12 text-brand-red"
        : "bg-brand-yellow/12 text-brand-yellow";
  return (
    <div className="admin-card rounded-sm p-5 relative overflow-hidden group hover:-translate-y-[2px] transition-transform duration-300">
      <div className="flex items-start justify-between">
        <p className="eyebrow-muted">{label}</p>
        <div className={`h-8 w-8 grid place-items-center rounded-sm ${accentBg}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <p className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight">
        {typeof value === "number" ? value.toLocaleString() : value ?? 0}
      </p>
      {note ? <p className="mt-2 text-[11px] text-brand-gray">{note}</p> : null}
      <div className="absolute inset-x-0 bottom-0 h-[2px] bg-brand-yellow scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-500" />
    </div>
  );
}

function QuickAction({ to, label }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-2 border border-white/10 hover:border-brand-yellow hover:text-brand-yellow rounded-sm px-4 py-2 text-xs uppercase tracking-[0.14em] font-medium bg-white/[0.02]"
    >
      {label}
      <ArrowUpRight className="h-3.5 w-3.5" />
    </Link>
  );
}

function EmptyState({ label, small = false }) {
  return (
    <div
      className={`h-full w-full grid place-items-center border border-dashed border-white/10 rounded-sm text-center px-6 ${small ? "py-6" : "py-10"} text-brand-gray text-sm`}
    >
      {label}
    </div>
  );
}

function HealthRow({ label, value, pending }) {
  const isPending = pending ? value === "Pending" : Number(value) > 0;
  return (
    <li className="flex items-center justify-between border-b border-white/6 pb-2 last:border-0">
      <span className="text-brand-white/90">{label}</span>
      <span
        className={`text-xs font-semibold px-2 py-0.5 rounded-sm ${
          isPending ? "bg-brand-red/15 text-brand-red" : "bg-brand-green/15 text-brand-green"
        }`}
      >
        {pending ? value : Number(value) > 0 ? value : "OK"}
      </span>
    </li>
  );
}
